import React, {useMemo, useCallback, useState} from 'react';
import styled from 'styled-components';
import {ComposableMap, Geographies, Geography, Marker} from "react-simple-maps";
import geography from "./features.json";
import coordinates from "./coordinates.json";
import {Country} from "types/analytics";
import {Dropdown} from './dropdown/dropdown';
import {getRandomColor} from "components/Analytics/utils";

interface MapProps {
    countries: Country[] | undefined;
}

interface CoordinatesData {
    [key: string]: number[][][];
}

interface GeometryObject {
    id?: string;
    iso2?: string;
    [key: string]: any;
}

const coordinatesData = coordinates as unknown as CoordinatesData;

export const Map: React.FC<MapProps> = ({countries}) => {
    const [tooltip, setTooltip] = useState<{x: number; y: number; content: string} | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);

    const handleEnter = useCallback((country: Country, evt: React.MouseEvent | React.TouchEvent) => {
        if (isCalculating) return;

        const target = evt.currentTarget;
        if (!target) return;

        const rect = target.getBoundingClientRect();
        const markerCenterX = rect.left + rect.width / 2;
        const markerCenterY = rect.top + rect.height / 2;

        setIsCalculating(true);

        requestAnimationFrame(() => {
            const tooltipWidth = 210;
            const tooltipHeight = 100;
            const offset = 10;
            const padding = 10;

            let x = markerCenterX + offset;
            let y = markerCenterY - tooltipHeight / 2;

            if (x + tooltipWidth > window.innerWidth - padding) {
                x = markerCenterX - tooltipWidth - offset;
            }

            if (x < padding) {
                x = padding;
            }

            if (y + tooltipHeight > window.innerHeight - padding) {
                y = window.innerHeight - tooltipHeight - padding;
            }

            if (y < padding) {
                y = padding;
            }

            setTooltip({
                x,
                y,
                content: country.name,
            });

            setIsCalculating(false);
        });
    }, [isCalculating]);

    const handleLeave = useCallback(() => {
        setIsCalculating(false);
        setTooltip(null);
    }, []);

    const handleMove = useCallback(() => {
        return;
    }, []);

    const countryWithColor = useMemo(() => {
        if (!countries?.length) return [];

        return countries.map(country => ({
            ...country,
            color: getRandomColor()
        }));
    }, [countries]);

    return (
        <>
            <Box>
                <span className="header">Analytics by region</span>
                <ViewMapLabel>
                    <span>View map by</span>
                    <Dropdown
                        options={[
                            {label: "Totals", value: "Totals"},
                            {label: "Territory", value: "Territory"},
                            {label: "Label", value: "Label"},
                            {label: "Artist", value: "Artist"},
                            {label: "Release", value: "Release"},
                            {label: "Track", value: "Track"},
                        ]}
                        onChange={(value) => console.log(value)}
                        initialValue='Totals'
                    />
                </ViewMapLabel>
            </Box>
            <Container>
                <Wrapper>
                    <ComposableMap
                        projection="geoMercator"
                        projectionConfig={{
                            scale: 160,
                            center: [0, 55]
                        }}
                        width={1100}
                        height={850}
                        style={{
                            width: "100%",
                            height: "auto"
                        }}
                    >
                        <Geographies geography={geography}>
                            {({geographies}) =>
                                geographies.map((geo) => (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        style={{
                                            default: {fill: "#F3F4F6", stroke: "#E5E7EB", strokeWidth: 0.5, outline: "none"},
                                            hover: {fill: "#F3F4F6", stroke: "#E5E7EB", strokeWidth: 0.5, outline: "none"},
                                            pressed: {fill: "#F3F4F6", stroke: "#E5E7EB", strokeWidth: 0.5, outline: "none"}
                                        }}
                                    />
                                ))
                            }
                        </Geographies>
                        {countryWithColor.map((country) => {
                            const geometry = geography?.objects?.world?.geometries?.find(
                                (g: GeometryObject) => g.iso2 === country.iso2
                            ) as GeometryObject | undefined;

                            if (!geometry?.id) return null;

                            // @ts-ignore
                            const rawCoordinate = coordinates[geometry.id];
                            if (!rawCoordinate?.[0]?.[0]) return null;

                            const coordinate: [number, number] = rawCoordinate[0][0];
                            if (!Array.isArray(coordinate) || coordinate.length < 2) return null;

                            return (
                                <Marker
                                    key={country.iso2}
                                    coordinates={coordinate}
                                    onMouseEnter={(e) => handleEnter(country, e)}
                                    onMouseMove={handleMove}
                                    onMouseLeave={handleLeave}
                                    onTouchStart={(e) => handleEnter(country, e)}
                                    onTouchMove={handleMove}
                                    onTouchEnd={handleLeave}
                                >
                                    <g className="marker-group">
                                        {/* Invisible larger circle for easier tapping */}
                                        <circle
                                            r={20}
                                            fill="transparent"
                                            style={{ cursor: 'pointer' }}
                                            className="touch-target"
                                        />
                                        {/* Visible marker with glow effect */}
                                        <circle
                                            r={5}
                                            fill={country.color}
                                            opacity={0.2}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                        <circle
                                            r={5}
                                            fill={country.color}
                                            stroke="#fff"
                                            strokeWidth={1}
                                            style={{ pointerEvents: 'none' }}
                                            className="visible-marker"
                                        />
                                    </g>
                                </Marker>
                            );
                        })}
                    </ComposableMap>
                </Wrapper>
                {tooltip && (
                    <Tooltip style={{top: tooltip.y, left: tooltip.x}}>
                        <div className="content">
                            {countryWithColor.map((country) => {
                                if (tooltip.content !== country.name) return null;

                                return (
                                    <div key={country.iso2} className="row">
                                        <div className="info">
                                            <span className="dot" style={{backgroundColor: country.color}} />
                                            <span className="label">{country.name}</span>
                                        </div>
                                        <span className="value">{country.total}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </Tooltip>
                )}
            </Container>
        </>
    );
};

export const Container = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        display: block !important;
        width: 100% !important;
        height: auto !important;
        max-width: 100%;
    }

    path {
        fill: #F3F4F6;
    }

    /* Add subtle animation to the map container */
    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    animation: fadeIn 0.5s ease-in;
`;

const Wrapper = styled.div`
    width: 55%;
    min-width: 0;
    height: auto;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        margin-top: -5rem;

        @media (max-width: 768px) {
            margin-top: 0;
        }
    }

    /* Increase marker size on mobile for easier tapping */
    @media (max-width: 768px) {
        .marker-group {
            .touch-target {
                r: 25;
            }
        }
    }

    /* Add smooth transitions to markers */
    .visible-marker {
        transition: all 0.2s ease;
    }

    .marker-group:hover .visible-marker {
        transform: scale(1.15);
    }
`;

const Tooltip = styled.div`
    position: fixed;
    width: 210px;
    max-width: calc(100vw - 20px);
    display: flex;
    flex-direction: column;
    pointer-events: none;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 0 10px 3px rgba(188, 188, 191, 0.68);
    z-index: 9999;

    .content {
        padding: 8px 12px;
        display: flex;
        gap: 15px;
        flex-direction: column;
    }

    .row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }

    .info {
        display: flex;
        align-items: center;
        gap: 4px;
        min-width: 0;
        flex: 1;
    }

    .dot {
        display: inline-block;
        width: 11px;
        height: 11px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    .label {
        font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 15px;
        color: #000000;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
    }

    .value {
        font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 15px;
        color: rgba(0, 0, 0, 0.5);
        flex-shrink: 0;
        white-space: nowrap;
    }
`;

const Box = styled.div`
    width: 100%;
    border-radius: 10px;
    height: 40px;
    padding: 4px 18px 4px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(246, 246, 246, 1);
    color: rgba(0, 0, 0, 0.31);
    font-weight: 600;
    margin-top: 1rem;

    @media (max-width: 768px) {
        padding-left: 0;
        padding-right: 0;

        .header {
            font-size: 13px;
        }
    }
`;

const ViewMapLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    color: rgba(0, 0, 0, 0.31);
    font-weight: 600;

    @media (max-width: 768px) {
        justify-content: space-between;
        gap: 7px;

        span {
            font-size: 13px;
        }
    }
`;